/**
 * /budget — Budget & MMM, D5 drawn from an uploaded Meridian model version (spec 2026-10-28-native-budget-mmm-design).
 * Composition only: the reads in useBudgetData, each box a card in components/budget, the maths in services/budget.
 * - Layout (spec §4.2): with no usable version, S, then 1 beside I; with one, S, 1, 2 | 3, 4, 5 | 6, 7, 8. The pairs sit
 *   side by side from lg. Until the version read settles, the first layout, with box 1 and box I in its state.
 * - A box whose reads are not all ready is D1's OverviewBox under the card's own id and title: loading, or the error
 *   with ลองอีกครั้ง, never 0 (spec §7). The master's all-workspaces view asks for one workspace in every box (C6, R23).
 * - Above box 2, a banner when a live check fails after the version (C4; check 6's ✕ is box S's own warning) and one
 *   when a quality tile is red (spec §5.2), under which box 7 recommends nothing.
 * - The simulator's allocation is React state, never the URL or storage, and each version starts at its current
 *   budget (C8). No export, no chat and no demo mode (C7). Roles per plan R28: box 1's training download is every
 *   member's (O13), its upload canEdit's (O14). The upload's "stored as vN" is page state too, since the re-read after
 *   it redraws box 1 (I1).
 *
 * @module pages/BudgetMmm
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { YDMPageHero } from '@/components/brand';
import { AccountAllocation } from '@/components/budget/AccountAllocation';
import { ActualVsPredicted } from '@/components/budget/ActualVsPredicted';
import { BudgetSimulator } from '@/components/budget/BudgetSimulator';
import { ContributionTable } from '@/components/budget/ContributionTable';
import { DecompositionChart } from '@/components/budget/DecompositionChart';
import { InterimCard } from '@/components/budget/InterimCard';
import { MoveAdvice } from '@/components/budget/MoveAdvice';
import { DownloadRefused, ReadinessCard } from '@/components/budget/ReadinessCard';
import { ResponseCurves } from '@/components/budget/ResponseCurves';
import { ScopeCard } from '@/components/budget/ScopeCard';
import { VersionUploadForm } from '@/components/budget/VersionUploadForm';
import { OverviewBox } from '@/components/overview/OverviewBox';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useBudgetData, type BudgetData } from '@/hooks/useBudgetData';
import { useChannelPageScope, useScopeArrival, type PageScope } from '@/hooks/useChannelPageScope';
import { allocationOf } from '@/services/budget/accountAllocation';
import { adviceOf, marginalAtCurrent, qualityOk } from '@/services/budget/advice';
import { downloadDataset, saveTextFile } from '@/services/budget/budgetAdminService';
import { OUTCOME_METRICS, STALE_DAYS, type OutcomeMetric } from '@/services/budget/budgetContract';
import { writeConfig, type ActiveVersion } from '@/services/budget/budgetReaders';
import { interimRowsOf } from '@/services/budget/interim';
import { checksOf, downloadAllowed, type ReadinessRow } from '@/services/budget/readinessRules';
import { scopeRowsOf } from '@/services/budget/scopeRows';
import { currentAllocation, type Allocation } from '@/services/budget/simulator';
import { fmtShortDate, type TFn } from '@/services/channels/channelFormat';
import { DEFAULT_SHARED } from '@/services/dashboardFilters';
import { dataDateOf, daysBetween } from '@/services/dataDate';
import { addDays, bangkokToday } from '@/services/looker/lookerDateRange';
import type { FactRow, ReadState } from '@/services/overview/overviewTypes';

const PAIR_CLASS = 'grid grid-cols-1 gap-4 lg:grid-cols-2';
const BANNER_CLASS = {
  live: 'rounded-lg border border-warning-700 px-3 py-2 text-sm text-warning-700',
  quality: 'rounded-lg border border-error-700 px-3 py-2 text-sm font-semibold text-error-700',
} as const;

interface Roles {
  canEdit: boolean;
  canImport: boolean;
}

/** R28: the workspace role as CanAccess reads it, and the master (useIsPlatformMaster's, through the page scope). */
const useRoles = (page: PageScope): Roles => {
  const role = useWorkspace().workspace?.userRole;
  const canEdit = page.isMaster || role === 'admin';
  return { canEdit, canImport: canEdit || role === 'analyst' };
};

type Waiting = 'loading' | 'failed' | 'needsWorkspace';

/** A box whose reads are not all ready: failed once one failed (spec §7), otherwise loading (idle = no scope yet). */
const waitingOf = (reads: ReadonlyArray<ReadState<unknown>>): Waiting =>
  (reads.some((r) => r.status === 'failed') ? 'failed' : 'loading');

/** D1's OverviewBox under the card's own id and title, for a box that has nothing to draw yet. */
const Pending: React.FC<{ id: string; title: string; state: Waiting; onRetry?: () => void }> = ({ id, title, state, onRetry }) => (
  <OverviewBox id={id} title={title} state={{ status: state }} onRetry={onRetry}>{() => null}</OverviewBox>
);

/** get_mmm_readiness's null is a workspace the caller cannot see: box 1's error, never seven ✕ or zeros. */
const seenOf = (r: ReadState<ReadinessRow | null>): ReadState<ReadinessRow> => {
  if (r.status !== 'ready') return r;
  return r.data === null ? { status: 'failed' } : { status: 'ready', data: r.data };
};

/** Check 7's one currency, which every live money figure is in (spec §5.9, R18); none, or several, is null. */
const scopeCurrencyOf = (r: ReadinessRow): string | null => (r.currencies.length === 1 ? r.currencies[0] : null);

/** Spec §5.9: box I reads only the scope currency's rows, and without one, none; box 8 only the version's. */
const inCurrency = (rows: readonly FactRow[], currency: string | null): FactRow[] =>
  (currency === null ? [] : rows.filter((r) => r.currency === currency));

/** Spec §5.12: the data's last day, the window's last Monday + 6 (plan R1). */
const dataEndOf = (v: ActiveVersion): string => addDays(v.windowEnd, 6);

/** The Bangkok day the trainer finished. */
const trainedOf = (v: ActiveVersion, t: TFn): string => fmtShortDate(dataDateOf(v.bundle.trainer.trained_at), t);

interface BoxProps {
  data: BudgetData;
  /** seenOf(data.readiness): R2's scope, its currency and the live checks. */
  readiness: ReadState<ReadinessRow>;
  roles: Roles;
  workspaceId: string;
}

/** The version box 1's upload stored, and in which workspace, so another workspace never shows it (I1). */
interface Stored { workspaceId: string; version: number }

/** Held by the page, above Body: a re-read unmounts box 1 and its form, and a first version moves it. */
interface UploadProps {
  stored: Stored | null;
  onStored: (s: Stored) => void;
}

/** Box S. Its warning compares the config with the version's scope, so it waits for the version read too. */
const ScopeBox: React.FC<BoxProps> = ({ data, roles, workspaceId }) => {
  const { t } = useTranslation();
  const { accounts, facts, config, active, refreshConfig } = data;
  if (accounts.status !== 'ready' || facts.status !== 'ready' || config.status !== 'ready' || active.status !== 'ready') {
    return <Pending id="budget-s" title={t('budget.scope.title')} state={waitingOf([accounts, facts, config, active])} onRetry={data.retry} />;
  }
  const confirmed = config.data;
  const version = active.data;
  // R3: the ticked ids with the confirmed metric, or revenue before the first confirm. A rejection reaches the card.
  const onConfirm = async (ids: string[]): Promise<void> => {
    await writeConfig(workspaceId, ids, confirmed?.outcomeMetric ?? 'revenue');
    refreshConfig();
  };
  return (
    <ScopeCard
      rows={scopeRowsOf(accounts.data, facts.data.current, confirmed?.scopeAccountIds ?? null)} canEdit={roles.canEdit}
      confirmedIds={confirmed?.scopeAccountIds ?? null} versionIds={version?.scopeAccountIds ?? null}
      metric={confirmed?.outcomeMetric ?? 'revenue'} versionMetric={version?.outcomeMetric ?? null} onConfirm={onConfirm}
    />
  );
};

/**
 * Box 1: the seven checks on today's data; with a version, its tiles, its line and its age (spec §4.2, §5.12, R16);
 * the training download for every member, and the upload for canEdit (O13, O14).
 */
const StatusBox: React.FC<BoxProps & UploadProps> = ({ data, readiness, roles, workspaceId, stored, onStored }) => {
  const { t } = useTranslation();
  const { config, active, refreshConfig } = data;
  if (readiness.status !== 'ready' || config.status !== 'ready' || active.status !== 'ready') {
    return <Pending id="budget-1" title={t('budget.status.title')} state={waitingOf([readiness, config, active])} onRetry={data.retry} />;
  }
  const confirmed = config.data;
  const version = active.data;
  const checks = checksOf(readiness.data, version);
  // R3: the confirmed scope with the new metric. The selector stays disabled until a config exists.
  const onMetric = async (metric: OutcomeMetric): Promise<void> => {
    if (confirmed === null) throw new Error('budget: confirm the scope first');
    await writeConfig(workspaceId, confirmed.scopeAccountIds, metric);
    refreshConfig();
  };
  // The route builds and records the file; its refusal reaches the card, a 409's with its failing check ids.
  const onDownload = async (): Promise<void> => {
    const res = await downloadDataset(workspaceId);
    if (!res.success || !res.data) throw new DownloadRefused(res.error ?? t('budgetAdmin.download.failed'), res.failing ?? []);
    saveTextFile(res.data.fileName, res.data.text);
  };
  // A stored version is active at once (R7), so retry() reads it again; the page keeps its number for the line.
  const onUploaded = (v: number): void => {
    onStored({ workspaceId, version: v });
    data.retry();
  };
  const uploadSlot = roles.canEdit ? <VersionUploadForm workspaceId={workspaceId} onUploaded={onUploaded} /> : null;
  const dataEnd = version && dataEndOf(version);
  const versionLine = version && t('budget.status.versionLine', {
    date: trainedOf(version, t), weeks: version.bundle.window.weeks, channels: version.bundle.channels.length,
  });
  return (
    <ReadinessCard
      checks={checks} metric={confirmed?.outcomeMetric ?? 'revenue'} hasConfig={confirmed !== null}
      canEdit={roles.canEdit} onMetric={onMetric} quality={version?.bundle.diagnostics ?? null} versionLine={versionLine}
      dataEnd={dataEnd} stale={dataEnd !== null && daysBetween(dataEnd, bangkokToday()) > STALE_DAYS}
      currency={scopeCurrencyOf(readiness.data)}
      canDownload={downloadAllowed(readiness.data, checks)} onDownload={onDownload} uploadSlot={uploadSlot}
      stored={stored !== null && stored.workspaceId === workspaceId && version?.version === stored.version ? stored.version : null}
    />
  );
};

/** Box I (spec §5.8), drawn only without a version: the scope's live channels in its currency, and why no MMM yet. */
const InterimBox: React.FC<BoxProps> = ({ data, readiness, roles }) => {
  const { t } = useTranslation();
  const { active, facts, mapping, windows } = data;
  if (readiness.status !== 'ready' || active.status !== 'ready' || facts.status !== 'ready' || mapping.status !== 'ready' || windows === null) {
    return <Pending id="budget-i" title={t('budget.interim.title')} state={waitingOf([readiness, active, facts, mapping])} onRetry={data.retry} />;
  }
  const failing = checksOf(readiness.data, active.data).filter((c) => !c.pass).map((c) => c.id);
  const currency = scopeCurrencyOf(readiness.data);
  const pair = { current: inCurrency(facts.data.current, currency), previous: inCurrency(facts.data.previous, currency) };
  // The readiness row carries the config's metric (revenue before a confirm), so box I waits for no config read.
  const metric = OUTCOME_METRICS.find((m) => m === readiness.data.outcomeMetric) ?? 'revenue';
  return (
    <InterimCard
      rows={interimRowsOf(pair, readiness.data.scopeAccountIds, mapping.data, { current: windows.cut, previous: windows.cutPrevious })}
      failing={failing} readyNoVersion={failing.length === 0} canImport={roles.canImport}
      scopeConfirmed={readiness.data.hasConfig} currency={currency} metric={metric}
    />
  );
};

/**
 * Box 8 (spec §5.7): box 6's channel budgets split over the version's scope, on the live 28 days (R17) in its currency.
 * A scope confirmed since is not the one those budgets were trained on (spec §5.10, T15d).
 */
const AllocationBox: React.FC<BoxProps & { version: ActiveVersion; allocation: Allocation }> = ({ data, version, allocation }) => {
  const { t } = useTranslation();
  const { accounts, facts, mapping } = data;
  if (accounts.status !== 'ready' || facts.status !== 'ready' || mapping.status !== 'ready') {
    return <Pending id="budget-8" title={t('budget.allocation.title')} state={waitingOf([accounts, facts, mapping])} onRetry={data.retry} />;
  }
  const { bundle } = version;
  return (
    <AccountAllocation
      rows={allocationOf(allocation, inCurrency(facts.data.current, bundle.currency), accounts.data, version.scopeAccountIds, mapping.data)}
      channelBudget={allocation} currency={bundle.currency} stopped={bundle.channels.filter((c) => c.stopped).map((c) => c.id)}
    />
  );
};

/** Boxes 2–8 of one version. Keyed by its id, so a new version starts the simulator again at its current budget (C8). */
const VersionBoxes: React.FC<BoxProps & { version: ActiveVersion }> = ({ version, ...box }) => {
  const { bundle } = version;
  const [allocation, setAllocation] = useState<Allocation>(() => currentAllocation(bundle));
  // A slider move redraws the page; these change only with the bundle.
  const marginal = useMemo(() => marginalAtCurrent(bundle), [bundle]);
  const advice = useMemo(() => adviceOf(bundle), [bundle]);
  return (
    <>
      <div className={PAIR_CLASS}>
        <ActualVsPredicted weekly={bundle.weekly} currency={bundle.currency} metric={bundle.outcome_metric} />
        <DecompositionChart weekly={bundle.weekly} channels={bundle.channels} currency={bundle.currency} metric={bundle.outcome_metric} />
      </div>
      <ContributionTable bundle={bundle} marginal={marginal} />
      <div className={PAIR_CLASS}>
        <ResponseCurves bundle={bundle} simulated={allocation} />
        <BudgetSimulator bundle={bundle} allocation={allocation} onChange={setAllocation} />
      </div>
      <MoveAdvice items={advice} currency={bundle.currency} metric={bundle.outcome_metric} />
      <AllocationBox {...box} version={version} allocation={allocation} />
    </>
  );
};

/** C4: a live check other than the scope fails (spec §5.1); spec §5.2: a red quality tile. */
const Banners: React.FC<{ version: ActiveVersion; readiness: ReadState<ReadinessRow> }> = ({ version, readiness }) => {
  const { t } = useTranslation();
  const live = readiness.status === 'ready' && checksOf(readiness.data, version).some((c) => c.id !== 'scope' && !c.pass);
  return (
    <>
      {live && (
        <p data-banner="live" role="status" className={BANNER_CLASS.live}>
          {t('budget.banner.live', {
            version: version.version, start: fmtShortDate(version.windowStart, t), end: fmtShortDate(dataEndOf(version), t),
          })}
        </p>
      )}
      {!qualityOk(version.bundle.diagnostics) && (
        <p data-banner="quality" role="status" className={BANNER_CLASS.quality}>{t('budget.banner.quality')}</p>
      )}
    </>
  );
};

/** Spec §4.2's two layouts, chosen by the version read. */
const Body: React.FC<Omit<BoxProps, 'readiness'> & UploadProps> = ({ stored, onStored, ...props }) => {
  const box: BoxProps = { ...props, readiness: seenOf(props.data.readiness) };
  const { active } = props.data;
  if (active.status === 'ready' && active.data !== null) {
    return (
      <>
        <ScopeBox {...box} />
        <StatusBox {...box} stored={stored} onStored={onStored} />
        <Banners version={active.data} readiness={box.readiness} />
        <VersionBoxes key={active.data.id} version={active.data} {...box} />
      </>
    );
  }
  return (
    <>
      <ScopeBox {...box} />
      <div className={PAIR_CLASS}>
        <StatusBox {...box} stored={stored} onStored={onStored} />
        <InterimBox {...box} />
      </div>
    </>
  );
};

/** Before any read: all workspaces asks for one (C6, R23); a null scope (no workspace, or the master status) loads. */
const Placeholder: React.FC<{ state: 'loading' | 'needsWorkspace' }> = ({ state }) => {
  const { t } = useTranslation();
  return (
    <>
      <Pending id="budget-s" title={t('budget.scope.title')} state={state} />
      <div className={PAIR_CLASS}>
        <Pending id="budget-1" title={t('budget.status.title')} state={state} />
        <Pending id="budget-i" title={t('budget.interim.title')} state={state} />
      </div>
    </>
  );
};

const BudgetMmm: React.FC = () => {
  const { t } = useTranslation();
  const page = useChannelPageScope();
  const data = useBudgetData(page.scope);
  const roles = useRoles(page);
  const [stored, setStored] = useState<Stored | null>(null);
  // Last, as on D2–D4. D5 has no filters, so a scope=all arrival writes the shared defaults: D1's 28 days, the window
  // boxes S, I and 8 read (R17).
  useScopeArrival(page, DEFAULT_SHARED);
  const workspaceId = page.scope?.workspaceId ?? null;
  const version = data.active.status === 'ready' ? data.active.data : null;
  return (
    <div className="space-y-4 p-4 md:p-6">
      <YDMPageHero
        eyebrow={page.showAll ? t('looker.allWorkspaces') : page.workspaceName ?? undefined}
        headline={t('budget.title')} subtitle={t('budget.subtitle')} className="mb-2"
        actions={version && (
          <p data-hero-version className="text-sm text-neutral-700">
            {t('budget.heroVersion', { version: version.version, trained: trainedOf(version, t), dataEnd: fmtShortDate(dataEndOf(version), t) })}
          </p>
        )}
      />
      {workspaceId === null
        ? <Placeholder state={page.showAll ? 'needsWorkspace' : 'loading'} />
        : <Body data={data} roles={roles} workspaceId={workspaceId} stored={stored} onStored={setStored} />}
    </div>
  );
};

export default BudgetMmm;
